import React, { FC, memo, useMemo } from 'react';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import DateRangeInput from '@zvuk-b2b/react-uikit/ui/DateInput/DateRangeInput';
import IconPlus from '@zvuk-b2b/react-uikit/ui/icons/Plus';

import { TEXTS } from './texts';
import {
  DATA_TEST_IDS,
  DATE_RANGE_DESKTOP_MONTHS_SHOWN,
  DATE_RANGE_MOBILE_MONTHS_SHOWN,
  MAX_DATE,
  MIN_DATE,
} from './consts';
import ReportRecipientsSelect from './ReportRecipientsSelect';
import ExternalReportRecipientsInput from './ExternalReportRecipientsInput';
import AddressReportTypeSelect from './AddressReportTypeSelect';
import AddressReportPointsSelect from './AddressReportPointsSelect';
import { AUDIO_REPORT_SCOPE } from './types';
import type { AudioAdCampaignsReportModalProps } from './types';

import styles from './index.module.scss';

const AudioAdCampaignsReportModal: FC<AudioAdCampaignsReportModalProps> = ({
  close,
  closeButtonText,
  endDate,
  externalRecipientEmails,
  isExternalRecipientsInputVisible,
  isMobile,
  isRequestDisabled,
  isReportAddressesLoading,
  loading,
  modalTitle,
  onDateRangeChange,
  onExternalRecipientEmailsChange,
  onReportScopeChange,
  onRequestSubmit,
  onSelectedAddressIdsChange,
  onSelectedRecipientIdsChange,
  onShowExternalRecipientsInput,
  recipients,
  reportAddresses,
  reportAddressTags,
  reportScope,
  requestButtonText,
  selectedAddressIds,
  selectedRecipientIds,
  startDate,
}) => {
  const dateRangeMonthsShown = isMobile ? DATE_RANGE_MOBILE_MONTHS_SHOWN : DATE_RANGE_DESKTOP_MONTHS_SHOWN;

  const actionButtons = useMemo(
    () => [
      <Button
        elementId="button_cancel_request_ad_campaign_report_modal"
        key="button_cancel_request_ad_campaign_report_modal"
        type={ButtonType.SECONDARY}
        className={styles.audioActionButton}
        onClick={close}>
        {closeButtonText}
      </Button>,
      <Button
        elementId="button_submit_request_ad_campaigns_report_modal"
        key="button_submit_request_ad_campaigns_report_modal"
        type={ButtonType.PRIMARY}
        className={styles.audioActionButton}
        onClick={onRequestSubmit}
        isLoading={loading}
        isDisabled={isRequestDisabled}>
        {requestButtonText}
      </Button>,
    ],
    [close, closeButtonText, isRequestDisabled, loading, onRequestSubmit, requestButtonText]
  );

  const addExternalRecipientsButton = !isExternalRecipientsInputVisible ? (
    <button
      type="button"
      data-testid={DATA_TEST_IDS.ADD_EXTERNAL_RECIPIENTS_BUTTON}
      className={styles.externalRecipientsButton}
      onClick={onShowExternalRecipientsInput}>
      <IconPlus className={styles.externalRecipientsButtonIcon} />
      <span data-testid={DATA_TEST_IDS.ADD_EXTERNAL_RECIPIENTS_TEXT}>{TEXTS.ADD_EXTERNAL_RECIPIENTS}</span>
    </button>
  ) : null;

  return (
    <ModalScreen
      title={modalTitle}
      onClose={close}
      elementId={DATA_TEST_IDS.AUDIO_MODAL}
      containerClassName={styles.audioContainer}
      contentClassName={styles.audioContent}
      contentElementId={DATA_TEST_IDS.AUDIO_MODAL_CONTENT}
      containerActionButtonsClassName={styles.audioActionButtons}
      actionButtons={actionButtons}>
      <div
        className={styles.audioDescription}
        data-testid={DATA_TEST_IDS.AUDIO_DESCRIPTION}>
        {TEXTS.REQUEST_AUDIO_REPORT_MODAL_CONTENT}
      </div>
      <div
        className={styles.audioDateRangeField}
        data-testid={DATA_TEST_IDS.AUDIO_DATE_RANGE_FIELD}>
        <label
          className={styles.audioFieldLabel}
          htmlFor="input_ad_campaigns_report_date_range"
          data-testid={DATA_TEST_IDS.AUDIO_DATE_RANGE_LABEL}>
          {TEXTS.PERIOD_LABEL}
        </label>
        <DateRangeInput
          elementId="input_ad_campaigns_report_date_range"
          startDate={startDate}
          endDate={endDate}
          onChange={onDateRangeChange}
          popperPlacement="bottom-start"
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          label={TEXTS.PERIOD_LABEL}
          containerClassName={styles.audioDateRangeInput}
          calendarButtonClassName={styles.audioDateRangeCalendarButton}
          clearButtonClassName={styles.audioDateRangeClearButton}
          popperClassName={styles.audioDateRangePopper}
          showPreviousMonths={false}
          openToDate={isMobile ? MIN_DATE : startDate || new Date()}
          monthsShown={dateRangeMonthsShown}
          focusSelectedMonth={!isMobile}
          isClearable
          withPortal={isMobile}
        />
      </div>
      <AddressReportTypeSelect
        value={reportScope}
        onChange={onReportScopeChange}
      />

      {reportScope === AUDIO_REPORT_SCOPE.ADDRESSES && (
        <AddressReportPointsSelect
          addresses={reportAddresses}
          isLoading={isReportAddressesLoading}
          tags={reportAddressTags}
          selectedAddressIds={selectedAddressIds}
          onChange={onSelectedAddressIdsChange}
        />
      )}

      <div
        className={styles.recipients}
        data-testid={DATA_TEST_IDS.AUDIO_RECIPIENTS}>
        <ReportRecipientsSelect
          afterTrigger={addExternalRecipientsButton}
          recipients={recipients}
          selectedRecipientIds={selectedRecipientIds}
          onChange={onSelectedRecipientIdsChange}
        />

        <ExternalReportRecipientsInput
          isVisible={isExternalRecipientsInputVisible}
          emails={externalRecipientEmails}
          onChange={onExternalRecipientEmailsChange}
        />
      </div>
    </ModalScreen>
  );
};

export default memo(AudioAdCampaignsReportModal);
